import { useState, useEffect, useCallback } from 'react';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { getAllThreadVisits } from '../lib/thread-visits-service';
import { useAuth } from '../lib/auth-context';

export function useUnreadMessages(): { hasUnread: boolean; refresh: () => void } {
  const { user } = useAuth();
  const [hasUnread, setHasUnread] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const refresh = useCallback(() => {
    setRefreshKey(prev => prev + 1);
  }, []);

  useEffect(() => {
    if (!user?.uid) {
      setHasUnread(false);
      return;
    }

    const messagesRef = collection(db, 'messages');
    const q = query(
      messagesRef,
      where('status', '==', 'active')
    );

    const unsubscribe = onSnapshot(q, async (snapshot) => {
      try {
        const visits = await getAllThreadVisits(user.uid);
        const visitedThreads = Object.keys(visits);

        if (visitedThreads.length === 0) {
          setHasUnread(false);
          return;
        }

        const messages = snapshot.docs.map(doc => doc.data());

        const unread = messages.some(msg => {
          const threadCode = msg.thread_code;
          const lastVisit = visits[threadCode];
          if (!lastVisit) return false;
          const createdAt = msg.created_at;
          if (!createdAt) return false;
          return createdAt.toMillis() > lastVisit.toMillis();
        });

        setHasUnread(unread);
      } catch {
        setHasUnread(false);
      }
    });

    return () => unsubscribe();
  }, [user?.uid, refreshKey]);

  return { hasUnread, refresh };
}
